<template>
  <div
    class="cms-page"
    :class="[pageTheme, isSectional ? 'sectional-page' : '']"
    :style="[!docIsASession ? { 'padding-bottom': '80px' } : '']"
    v-if="
      (doc && doc.type === type && doc.uid == uid) || (type === 'cms-smart-journal-page' && !uid)
    "
  >
    <div class="text-big">
      <!-- Resource page -->
      <CmsTextPage v-if="!!doc && isTextPage" :doc="doc" />

      <!-- resource playlist -->
      <ResourcePlaylist v-if="type === 'cms-resource-playlist'" :doc="doc" />
      <template v-if="!!doc && type === 'cms-report-page'">
        <CmsReport :doc="doc" />
      </template>

      <template v-if="!!doc && type === 'cms-team-goal-capture-page'">
        <CmsGoalCapturePage :doc="doc" />
      </template>

      <template v-if="type === 'cms-resource-theme'">
        <ResourceThemesList :doc="doc" />
      </template>

      <template v-if="type === 'cms-resource-themes-page'">
        <div style="height: 10px"></div>
        <ResourceThemesPage v-if="doc" :doc="doc" />
      </template>

      <template v-if="!!doc && type === 'cms-session-with-slides'">
        <SessionWrapper>
          <CmsSession :doc="doc" />
        </SessionWrapper>
      </template>

      <template v-if="type === 'cms-pathway'">
        <PathwayWheel :doc="doc" />
      </template>

      <template v-if="!!doc && type === 'cms-personal-pathway'">
        <PersonalPathwayWheel :doc="doc" />
      </template>

      <template v-if="type === 'cms-team-effectiveness-review'">
        <TeamEffectivenessQuestionnairePage :doc="doc" />
      </template>

      <template v-if="type === 'cms-report'">
        <TeamEffectivenessReport :doc="doc" />
      </template>

      <template v-if="!!doc && type === 'cms-develop-page'">
        <CmsDevelopStepPage :doc="doc" />
      </template>

      <template v-if="!!doc && type === 'cms-pathway-step'">
        <SessionWrapper>
          <CmsSession :doc="doc" />
        </SessionWrapper>
      </template>

      <template v-if="!!doc && type === 'cms-personal-pathway-step'">
        <SessionWrapper>
          <CmsSession :doc="doc" />
        </SessionWrapper>
      </template>

      <template v-if="type === 'cms-smart-journal-page'">
        <SmartJournal :doc="doc"></SmartJournal>
      </template>

      <template v-if="type === 'cms-smart-text-box'">
        <div style="max-width: 650px; margin: 50px auto">
          <CmsSmartTextBox :uid="doc ? doc.uid : ''"></CmsSmartTextBox>
        </div>
      </template>

      <template v-if="type === 'cms-smart-callout'">
        <div style="max-width: 650px; margin: 50px auto">
          <CmsSmartCallout :uid="doc?.uid ?? ''"></CmsSmartCallout>
        </div>
      </template>
    </div>
  </div>
  <div class="page-placeholder" v-else>
    <!-- loading and not found -->
    <h2 class="text-danger" v-if="notFound">Not found.</h2>
  </div>
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import { computed, ref, watch } from "vue";
import useNotification, { CompletionNotification } from "../../composables/useNotification";
import { csContext } from "../../services/cs-context";
import CmsReport from "./cms-report.vue";
import TeamEffectivenessQuestionnairePage from "../team-effectiveness/team-effectiveness-questionnaire-page.vue";
import TeamEffectivenessReport from "../team-effectiveness/team-effectiveness-report.vue";
import CmsDevelopStepPage from "./cms-develop-step-page.vue";
import {
  CmsDocument,
  CmsDocumentTypes,
  CmsGoalCapturePage as CmsGoalCapturePageType,
  CmsPersonalPathway,
  CmsReportResource,
  CmsResourceData,
} from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import ResourcePlaylist from "./resource-playlist.vue";
import ResourceThemesList from "../../components/cms/resource-themes-list.vue";
import ResourceThemesPage from "./resource-themes-page.vue";
import SessionWrapper from "../../components/sessions/session-wrapper.vue";
import CmsSession from "./cms-session.vue";
import PathwayWheel from "./pathway-wheel.vue";
import CmsTextPage from "./cms-text-page.vue";
import PersonalPathwayWheel from "./personal-pathway-wheel.vue";
import SmartJournal from "../../components/smart-journal/smart-journal.vue";
import CmsSmartTextBox from "../../components/cms/cms-smart-text-box.vue";
import CmsSmartCallout from "../../components/cms/cms-smart-callout.vue";
import CmsGoalCapturePage from "./cms-goal-capture-page.vue";

const debug = d("cs.CmsPage");

const props = withDefaults(defineProps<{ uid?: string | null; type?: CmsDocumentTypes | null }>(), {
  uid: null,
  type: null,
});

const doc = ref<CmsDocument | null>(null);

const notFound = ref(false);

const isTextPage = computed(() => {
  if (!doc.value) {
    return false;
  }

  return doc.value.type === CmsDocumentTypes.CmsResource;
});

const docIsASession = computed(() => {
  switch (doc.value?.type) {
    case CmsDocumentTypes.CmsSession:
    case CmsDocumentTypes.CmsSessionWithSlides:
    case CmsDocumentTypes.CmsPersonalPathwayStep:
    case CmsDocumentTypes.CmsTeamPathwayStep:
      return true;
    default:
      return false;
  }
});

const pageTheme = computed(() => {
  // Note that doc is not necessarily a CmsResource, we're just using it as a typing example.
  return (doc.value?.data as CmsResourceData)?.page_visual_theme;
});

const isSectional = computed(() => {
  return doc.value?.type === CmsDocumentTypes.CmsReport;
});

function notifyTeamLeader() {
  if (!doc.value) return;

  const teamId = csContext.currentTeam?.id;
  if (!teamId) return;

  const { createCompletionNotification } = useNotification();

  const notification: CompletionNotification = {
    userId: csContext.user.id,
    teamId,
    prismicDoc: doc.value,
  };

  createCompletionNotification(notification);
}

const lastType = ref<string | null>(null);
const lastUid = ref<string | null>(null);

async function changeDoc() {
  if (props.type && props.uid && (props.type !== lastType.value || props.uid !== lastUid.value)) {
    debug("CmsPage.changeDoc");

    lastType.value = props.type;
    lastUid.value = props.uid;
    try {
      doc.value = await cms.getByUid(props.type, props.uid);

      if (doc.value) {
        notifyTeamLeader();
        if (doc.value.type === CmsDocumentTypes.CmsLinkResource) {
          window.location.href = cms.linkResolver(doc.value);
          return;
        }

        notFound.value = false;
      } else {
        throw new Error("Doc not found");
      }
    } catch {
      doc.value = null;
      notFound.value = true;
    }
  }
}

watch(
  [() => props.type, () => props.uid],
  () => {
    changeDoc();
  },
  { immediate: true },
);
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.cms-page {
  .page-placeholder {
    min-height: 100vh;
  }

  // Removed this so that strength mapping in sessions doesn't having padding at the bottom
  //padding-bottom: 80px;

  &.sectional-page {
    padding-bottom: 0; // The padding interferes with the alternating sections styling
  }

  overflow: hidden;
}
</style>
